import React, { useMemo } from "react";
import Helper from "../../Helper";
import { MapObject } from "../../models/MapObject";
import { CommandProjectInfo } from "../CommandTypes";

interface CommandMapLegendProps {
    project: CommandProjectInfo;
    data: {
        areas: {
            id: number;
            area_name: string;
        }[];
        equipment: {
            id: number;
            equipment_name: string;
        }[];
        map_objects: {
            id: number;
            project_id: number;
            mappable_type: string;
            mappable_id: number;
            uuid: string;
            geometry: "point" | "lineString" | "polygon";
            color: string;
            outline: string;
            opacity: number;
            icon: string;
            icon_anchor: string;
            icon_width: number;
            icon_units: string;
            coordinates: [number, number];
            created_at: string;
            updated_at: string;
            archived: boolean;
            radiuses: any[];
        }[];
    };
}

const CommandMapLegend: React.FC<CommandMapLegendProps> = ({ project, data }) => {
    if (!data || !data.areas || !data.equipment) {
        return null;
    }

    const mapObjects = useMemo(() => {
        return data.map_objects.map((obj) => MapObject.fromApiObject(obj, project.timezone));
    }, [data.map_objects]);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    React.useEffect(() => {
        const checkOverflow = () => {
            if (containerRef.current) {
                setIsOverflowing(containerRef.current.scrollHeight > containerRef.current.clientHeight);
            }
        };

        checkOverflow();
        window.addEventListener("resize", checkOverflow);
        return () => window.removeEventListener("resize", checkOverflow);
    }, [data.areas, data.equipment, mapObjects]);

    return (
        <div className="shrink-0 rounded-b-lg border border-t-0 p-4 pt-2 bg-gray-50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700">
            <div className="flex flex-row items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-plot-darkmode-white w-full">
                    Locations and Equipment
                </h4>
            </div>
            {data.areas.length == 0 && data.equipment.length == 0 && (
                <div className="w-full text-center italic">
                    No Locations or Equipment Found. Try adjusting your filters or search terms.
                </div>
            )}

            <div className="relative">
                <div
                    ref={containerRef}
                    className={Helper.classNames(
                        "flex flex-row gap-6 flex-wrap text-xs w-full text-gray-600 dark:text-neutral-300 transition-all duration-100 relative max-h-14 overflow-hidden"
                    )}
                >
                    {data.areas
                        .sort((a, b) => a.area_name.localeCompare(b.area_name))
                        .map((area, idx) => (
                            <div
                                key={`${area.id}-${idx}`}
                                className="flex items-center gap-1 hover:cursor-pointer hover:scale-110 transition-all duration-200"
                            >
                                {Helper.areaAvatar(
                                    area,
                                    mapObjects.find((obj) => obj.mappableId === area.id && obj.mappableType === "Area")
                                )}
                                <span>{area.area_name}</span>
                            </div>
                        ))}
                    {data.equipment
                        ?.sort((a, b) => a.equipment_name.localeCompare(b.equipment_name))
                        .map((equipment, idx) => (
                            <div
                                key={`${equipment.id}-${idx}`}
                                className="flex items-center gap-1 hover:cursor-pointer hover:scale-110 transition-all duration-200"
                            >
                                {Helper.areaAvatar(
                                    equipment,
                                    mapObjects.find(
                                        (obj) => obj.mappableId === equipment.id && obj.mappableType === "Equipment"
                                    )
                                )}
                                <span>{equipment.equipment_name}</span>
                            </div>
                        ))}
                </div>
                {isOverflowing && (
                    <div className="text-xs text-gray-500 dark:text-neutral-400 italic text-center mt-1">
                        More hidden
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommandMapLegend;
